import { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Grid, Card, CardContent, Chip,
  IconButton, CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, InputAdornment, Tabs, Tab,
} from '@mui/material';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { roomApi, roomTypeApi, Room, RoomType, getEffectiveRates } from '../../services/hotelApi';

const Rooms = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isFr = language === 'fr';
  const [tabValue, setTabValue] = useState(0);

  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Room form
  const [openRoomForm, setOpenRoomForm] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [roomForm, setRoomForm] = useState({
    roomNumber: '', roomTypeId: '', floor: '', status: 'available' as string,
    capacity: '', description: '',
    customHourlyRate: '', customNightRate: '', customFullDayRate: '',
  });
  const [saving, setSaving] = useState(false);

  // Room Type form
  const [openTypeForm, setOpenTypeForm] = useState(false);
  const [editingType, setEditingType] = useState<RoomType | null>(null);
  const [typeForm, setTypeForm] = useState({
    name: '', description: '',
    hourlyRate: '', nightRate: '', fullDayRate: '',
    capacity: '', amenities: '',
  });

  const currency = user?.company?.signCurrency || '$';

  const t = isFr ? {
    title: 'Chambres', subtitle: 'Gérer les chambres et types de chambres',
    rooms: 'Chambres', roomTypes: 'Types de Chambres',
    addRoom: 'Ajouter une chambre', editRoom: 'Modifier la chambre',
    addType: 'Ajouter un type', editType: 'Modifier le type',
    roomNumber: 'N° Chambre', roomType: 'Type', floor: 'Étage', status: 'Statut',
    name: 'Nom', description: 'Description', capacity: 'Capacité',
    hourlyRate: 'Tarif horaire', nightRate: 'Tarif nuit', fullDayRate: 'Tarif journée',
    customRates: 'Tarifs personnalisés (optionnel)',
    amenities: 'Équipements (séparés par des virgules)',
    save: 'Enregistrer', cancel: 'Annuler', delete: 'Supprimer',
    available: 'Disponible', occupied: 'Occupée', reserved: 'Réservée', maintenance: 'Maintenance',
    actions: 'Actions', noData: 'Aucune donnée', confirmDelete: 'Êtes-vous sûr de vouloir supprimer ?',
    effectiveRates: 'Tarifs effectifs', people: 'pers.',
  } : {
    title: 'Rooms', subtitle: 'Manage rooms and room types',
    rooms: 'Rooms', roomTypes: 'Room Types',
    addRoom: 'Add Room', editRoom: 'Edit Room',
    addType: 'Add Type', editType: 'Edit Type',
    roomNumber: 'Room Number', roomType: 'Type', floor: 'Floor', status: 'Status',
    name: 'Name', description: 'Description', capacity: 'Capacity',
    hourlyRate: 'Hourly Rate', nightRate: 'Night Rate', fullDayRate: 'Full Day Rate',
    customRates: 'Custom Rates (optional)',
    amenities: 'Amenities (comma separated)',
    save: 'Save', cancel: 'Cancel', delete: 'Delete',
    available: 'Available', occupied: 'Occupied', reserved: 'Reserved', maintenance: 'Maintenance',
    actions: 'Actions', noData: 'No data', confirmDelete: 'Are you sure you want to delete?',
    effectiveRates: 'Effective Rates', people: 'ppl',
  };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true); setError('');
      const [r, rt] = await Promise.all([roomApi.getAll(), roomTypeApi.getAll()]);
      setRooms(Array.isArray(r) ? r : []);
      setRoomTypes(Array.isArray(rt) ? rt : []);
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  };

  // Room CRUD
  const handleOpenRoomForm = (room?: Room) => {
    if (room) {
      setEditingRoom(room);
      setRoomForm({
        roomNumber: room.roomNumber,
        roomTypeId: typeof room.roomTypeId === 'object' ? (room.roomTypeId as RoomType)._id : room.roomTypeId as string,
        floor: room.floor?.toString() || '',
        status: room.status,
        capacity: room.capacity?.toString() || '',
        description: room.description || '',
        customHourlyRate: room.customHourlyRate?.toString() || '',
        customNightRate: room.customNightRate?.toString() || '',
        customFullDayRate: room.customFullDayRate?.toString() || '',
      });
    } else {
      setEditingRoom(null);
      setRoomForm({ roomNumber: '', roomTypeId: '', floor: '', status: 'available', capacity: '', description: '', customHourlyRate: '', customNightRate: '', customFullDayRate: '' });
    }
    setOpenRoomForm(true);
  };

  const handleSaveRoom = async () => {
    try {
      setSaving(true);
      const data: any = { roomNumber: roomForm.roomNumber, roomTypeId: roomForm.roomTypeId, status: roomForm.status };
      if (roomForm.floor) data.floor = Number(roomForm.floor);
      if (roomForm.capacity) data.capacity = Number(roomForm.capacity);
      if (roomForm.description) data.description = roomForm.description;
      if (roomForm.customHourlyRate) data.customHourlyRate = Number(roomForm.customHourlyRate);
      if (roomForm.customNightRate) data.customNightRate = Number(roomForm.customNightRate);
      if (roomForm.customFullDayRate) data.customFullDayRate = Number(roomForm.customFullDayRate);

      if (editingRoom) { await roomApi.update(editingRoom._id, data); }
      else { await roomApi.create(data); }
      setOpenRoomForm(false);
      await loadData();
    } catch (err: any) { setError(err.message); } finally { setSaving(false); }
  };

  const handleDeleteRoom = async (id: string) => {
    if (!window.confirm(t.confirmDelete)) return;
    try { await roomApi.delete(id); await loadData(); } catch (err: any) { setError(err.message); }
  };

  // Room Type CRUD
  const handleOpenTypeForm = (type?: RoomType) => {
    if (type) {
      setEditingType(type);
      setTypeForm({
        name: type.name, description: type.description || '',
        hourlyRate: type.hourlyRate?.toString() || '',
        nightRate: type.nightRate?.toString() || '',
        fullDayRate: type.fullDayRate?.toString() || '',
        capacity: type.capacity?.toString() || '',
        amenities: (type.amenities || []).join(', '),
      });
    } else {
      setEditingType(null);
      setTypeForm({ name: '', description: '', hourlyRate: '', nightRate: '', fullDayRate: '', capacity: '', amenities: '' });
    }
    setOpenTypeForm(true);
  };

  const handleSaveType = async () => {
    try {
      setSaving(true);
      const data: any = {
        name: typeForm.name,
        description: typeForm.description || undefined,
        hourlyRate: Number(typeForm.hourlyRate) || 0,
        nightRate: Number(typeForm.nightRate) || 0,
        fullDayRate: Number(typeForm.fullDayRate) || 0,
        capacity: Number(typeForm.capacity) || 1,
        amenities: typeForm.amenities ? typeForm.amenities.split(',').map(s => s.trim()).filter(Boolean) : [],
      };
      if (editingType) { await roomTypeApi.update(editingType._id, data); }
      else { await roomTypeApi.create(data); }
      setOpenTypeForm(false);
      await loadData();
    } catch (err: any) { setError(err.message); } finally { setSaving(false); }
  };

  const handleDeleteType = async (id: string) => {
    if (!window.confirm(t.confirmDelete)) return;
    try { await roomTypeApi.delete(id); await loadData(); } catch (err: any) { setError(err.message); }
  };

  const getStatusColor = (s: string) => ({ available: '#4caf50', occupied: '#f44336', reserved: '#ff9800', maintenance: '#9e9e9e' }[s] || '#9e9e9e');
  const getStatusLabel = (s: string) => ({ available: t.available, occupied: t.occupied, reserved: t.reserved, maintenance: t.maintenance }[s] || s);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D3C4C' }}>{t.title}</Typography>
        <Typography variant="body1" color="text.secondary">{t.subtitle}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={`${t.rooms} (${rooms.length})`} sx={{ textTransform: 'none' }} />
        <Tab label={`${t.roomTypes} (${roomTypes.length})`} sx={{ textTransform: 'none' }} />
      </Tabs>

      {/* ROOMS TAB */}
      {tabValue === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => handleOpenRoomForm()}
              sx={{ backgroundColor: '#0D3C4C', textTransform: 'none', '&:hover': { backgroundColor: '#164B61' } }}>{t.addRoom}</Button>
          </Box>
          {rooms.length === 0 ? <Alert severity="info">{t.noData}</Alert> : (
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t.roomNumber}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.roomType}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.floor}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.capacity}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.effectiveRates}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.status}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.actions}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rooms.map((room) => {
                      const rates = getEffectiveRates(room);
                      return (
                        <TableRow key={room._id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{room.roomNumber}</TableCell>
                          <TableCell>{typeof room.roomTypeId === 'object' ? (room.roomTypeId as RoomType).name : '-'}</TableCell>
                          <TableCell>{room.floor || '-'}</TableCell>
                          <TableCell>{room.capacity || '-'} {t.people}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip label={`H: ${rates.hourlyRate.toLocaleString()}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                              <Chip label={`N: ${rates.nightRate.toLocaleString()}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                              <Chip label={`J: ${rates.fullDayRate.toLocaleString()}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={getStatusLabel(room.status)} size="small"
                              sx={{ backgroundColor: `${getStatusColor(room.status)}20`, color: getStatusColor(room.status), fontWeight: 600 }} />
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => handleOpenRoomForm(room)}><Edit2 size={16} /></IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteRoom(room._id)}><Trash2 size={16} /></IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </>
      )}

      {/* ROOM TYPES TAB */}
      {tabValue === 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" startIcon={<Plus size={18} />} onClick={() => handleOpenTypeForm()}
              sx={{ backgroundColor: '#0D3C4C', textTransform: 'none', '&:hover': { backgroundColor: '#164B61' } }}>{t.addType}</Button>
          </Box>
          {roomTypes.length === 0 ? <Alert severity="info">{t.noData}</Alert> : (
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t.name}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.description}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.capacity}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{t.hourlyRate}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{t.nightRate}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">{t.fullDayRate}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.amenities}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t.actions}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roomTypes.map((type) => (
                      <TableRow key={type._id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{type.name}</TableCell>
                        <TableCell>{type.description || '-'}</TableCell>
                        <TableCell>{type.capacity || '-'} {t.people}</TableCell>
                        <TableCell align="right">{(type.hourlyRate || 0).toLocaleString()} {currency}</TableCell>
                        <TableCell align="right">{(type.nightRate || 0).toLocaleString()} {currency}</TableCell>
                        <TableCell align="right">{(type.fullDayRate || 0).toLocaleString()} {currency}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {(type.amenities || []).map((a, i) => <Chip key={i} label={a} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />)}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => handleOpenTypeForm(type)}><Edit2 size={16} /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteType(type._id)}><Trash2 size={16} /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}
        </>
      )}

      {/* ROOM FORM DIALOG */}
      <Dialog open={openRoomForm} onClose={() => setOpenRoomForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editingRoom ? t.editRoom : t.addRoom}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label={t.roomNumber} value={roomForm.roomNumber} required size="small"
                onChange={e => setRoomForm(p => ({ ...p, roomNumber: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>{t.roomType}</InputLabel>
                <Select value={roomForm.roomTypeId} label={t.roomType}
                  onChange={e => setRoomForm(p => ({ ...p, roomTypeId: e.target.value }))}>
                  {roomTypes.map(type => <MenuItem key={type._id} value={type._id}>{type.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t.floor} type="number" value={roomForm.floor} size="small"
                onChange={e => setRoomForm(p => ({ ...p, floor: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t.capacity} type="number" value={roomForm.capacity} size="small"
                onChange={e => setRoomForm(p => ({ ...p, capacity: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t.status}</InputLabel>
                <Select value={roomForm.status} label={t.status}
                  onChange={e => setRoomForm(p => ({ ...p, status: e.target.value }))}>
                  <MenuItem value="available">{t.available}</MenuItem>
                  <MenuItem value="occupied">{t.occupied}</MenuItem>
                  <MenuItem value="reserved">{t.reserved}</MenuItem>
                  <MenuItem value="maintenance">{t.maintenance}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label={t.description} value={roomForm.description} size="small" multiline rows={2}
                onChange={e => setRoomForm(p => ({ ...p, description: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{t.customRates}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t.hourlyRate} type="number" value={roomForm.customHourlyRate} size="small"
                onChange={e => setRoomForm(p => ({ ...p, customHourlyRate: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t.nightRate} type="number" value={roomForm.customNightRate} size="small"
                onChange={e => setRoomForm(p => ({ ...p, customNightRate: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t.fullDayRate} type="number" value={roomForm.customFullDayRate} size="small"
                onChange={e => setRoomForm(p => ({ ...p, customFullDayRate: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenRoomForm(false)} sx={{ textTransform: 'none' }}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleSaveRoom}
            disabled={saving || !roomForm.roomNumber || !roomForm.roomTypeId}
            sx={{ backgroundColor: '#0D3C4C', textTransform: 'none', '&:hover': { backgroundColor: '#164B61' } }}>
            {saving ? <CircularProgress size={20} /> : t.save}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ROOM TYPE FORM DIALOG */}
      <Dialog open={openTypeForm} onClose={() => setOpenTypeForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{editingType ? t.editType : t.addType}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label={t.name} value={typeForm.name} required size="small"
                onChange={e => setTypeForm(p => ({ ...p, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t.capacity} type="number" value={typeForm.capacity} size="small"
                onChange={e => setTypeForm(p => ({ ...p, capacity: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label={t.description} value={typeForm.description} size="small" multiline rows={2}
                onChange={e => setTypeForm(p => ({ ...p, description: e.target.value }))} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t.hourlyRate} type="number" value={typeForm.hourlyRate} required size="small"
                onChange={e => setTypeForm(p => ({ ...p, hourlyRate: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t.nightRate} type="number" value={typeForm.nightRate} required size="small"
                onChange={e => setTypeForm(p => ({ ...p, nightRate: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label={t.fullDayRate} type="number" value={typeForm.fullDayRate} required size="small"
                onChange={e => setTypeForm(p => ({ ...p, fullDayRate: e.target.value }))}
                InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label={t.amenities} value={typeForm.amenities} size="small"
                onChange={e => setTypeForm(p => ({ ...p, amenities: e.target.value }))}
                helperText={isFr ? 'Ex: WiFi, TV, Climatisation' : 'Ex: WiFi, TV, Air Conditioning'} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenTypeForm(false)} sx={{ textTransform: 'none' }}>{t.cancel}</Button>
          <Button variant="contained" onClick={handleSaveType}
            disabled={saving || !typeForm.name || !typeForm.hourlyRate || !typeForm.nightRate || !typeForm.fullDayRate}
            sx={{ backgroundColor: '#0D3C4C', textTransform: 'none', '&:hover': { backgroundColor: '#164B61' } }}>
            {saving ? <CircularProgress size={20} /> : t.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Rooms;